import { useRef, useEffect, useCallback, useState } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';

const PostFormStep2Canvas = ({ onSubmit, onBack, onClose, submitting, error, stepBar, initialDataUrl, isCollapsed = false }) => {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPos = useRef(null);
  const hasNewStrokes = useRef(false);
  const historyRef = useRef([]);
  const historyStepRef = useRef(0);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [canClear, setCanClear] = useState(false);
  const [strokeColor, setStrokeColor] = useState('#7c3aed');
  const [showConfirm, setShowConfirm] = useState(false); // 投稿前確認モーダル
  const [canvasError, setCanvasError] = useState(null); // 空サイン時エラー

  const COLORS = [
    { color: '#7c3aed', label: '紫' },
    { color: '#1d4ed8', label: '青' },
    { color: '#0891b2', label: '水色' },
    { color: '#16a34a', label: '緑' },
    { color: '#ca8a04', label: '黄' },
    { color: '#ea580c', label: 'オレンジ' },
    { color: '#dc2626', label: '赤' },
    { color: '#db2777', label: 'ピンク' },
    { color: '#292524', label: '黒' },
    { color: '#ffffff', label: '白', border: true },
  ];

  // キャンバスサイズをコンテナに合わせる
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const container = canvas.parentElement;
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = strokeColorRef.current;
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const initHistory = (firstSnapshot) => {
      historyRef.current = [firstSnapshot];
      historyStepRef.current = 0;
      setCanUndo(false);
      setCanRedo(false);
      setCanClear(false);
    };

    if (initialDataUrl) {
      // 保存済み内容を復元してから履歴を初期化
      const img = new Image();
      img.src = initialDataUrl;
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        initHistory(canvas.toDataURL('image/png'));
        // 内容があるのでクリア・Undoを有効化
        setCanClear(true);
        setCanUndo(false); // 復元直後はUndoなし（これが最初の状態）
      };
    } else {
      initHistory(canvas.toDataURL('image/png'));
    }
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const handleColorChange = (color) => {
    strokeColorRef.current = color;
    setStrokeColor(color);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) ctx.strokeStyle = color;
  };

  const startDraw = useCallback((e) => {
    e.preventDefault();
    isDrawing.current = true;
    const pos = getPos(e);
    lastPos.current = pos;
    const ctx = canvasRef.current.getContext('2d');
    ctx.strokeStyle = strokeColorRef.current; // 最新色を必ず適用
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  }, []);

  const draw = useCallback((e) => {
    e.preventDefault();
    if (!isDrawing.current) return;
    const pos = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    hasNewStrokes.current = true; // 実際に描画が行われた
  }, []);

  const endDraw = useCallback(() => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    lastPos.current = null;

    // 実際にストロークが引かれた場合のみ履歴を保存
    if (!hasNewStrokes.current) return;
    hasNewStrokes.current = false;

    const canvas = canvasRef.current;
    historyStepRef.current += 1;
    historyRef.current = historyRef.current.slice(0, historyStepRef.current);
    historyRef.current.push(canvas.toDataURL('image/png'));

    setCanUndo(historyStepRef.current > 0);
    setCanRedo(false);
    setCanClear(true);
  }, []);

  const strokeColorRef = useRef('#7c3aed'); // stateと同期させるref（コールバック内で最新値を参照）

  const restoreCtxSettings = (ctx) => {
    ctx.strokeStyle = strokeColorRef.current;
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    restoreCtxSettings(ctx);
    historyStepRef.current += 1;
    historyRef.current = historyRef.current.slice(0, historyStepRef.current);
    historyRef.current.push(canvas.toDataURL('image/png'));
    setCanUndo(historyStepRef.current > 0);
    setCanRedo(false);
    setCanClear(false); // クリア後は何もないので非活性
  };

  const handleUndo = () => {
    if (historyStepRef.current <= 0) return;
    historyStepRef.current -= 1;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.src = historyRef.current[historyStepRef.current];
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      restoreCtxSettings(ctx);
      const step = historyStepRef.current;
      const total = historyRef.current.length;
      // 現在のスナップショットが空か確認
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      const isEmpty = !data.some((v, i) => i % 4 === 3 && v > 0);
      setCanUndo(step > 0);
      setCanRedo(step < total - 1);
      setCanClear(!isEmpty);
    };
  };

  const handleRedo = () => {
    if (historyStepRef.current >= historyRef.current.length - 1) return;
    historyStepRef.current += 1;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.src = historyRef.current[historyStepRef.current];
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      restoreCtxSettings(ctx);
      const step = historyStepRef.current;
      const total = historyRef.current.length;
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      const isEmpty = !data.some((v, i) => i % 4 === 3 && v > 0);
      setCanUndo(step > 0);
      setCanRedo(step < total - 1);
      setCanClear(!isEmpty);
    };
  };

  const handleSubmit = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    const isEmpty = !data.some((v, i) => i % 4 === 3 && v > 0);
    if (isEmpty) {
      setCanvasError('イラストまたはサインを描いてください');
      return;
    }
    setCanvasError(null);
    setShowConfirm(true); // 確認モーダルを表示
  };

  const handleConfirmedSubmit = () => {
    // showConfirm は閉じずに維持 → submitting=true 中はモーダル内でスピナーを表示し続ける
    onSubmit(canvasRef.current.toDataURL('image/png'));
  };

  // 投稿エラー時: submitting が false に戻ったらモーダルを閉じてエラーをキャンバス画面に表示
  useEffect(() => {
    if (!submitting && showConfirm && error) {
      setShowConfirm(false);
    }
  }, [submitting, showConfirm, error]);

  const handleBack = () => {
    // 戻る時に現在のキャンバス内容を保存して渡す
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onBack(dataUrl);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 mx-auto flex w-full max-w-lp flex-col rounded-t-2xl bg-white shadow-xl"
      style={{ height: '85dvh' }}
    >
      {/* 投稿前確認モーダル */}
      {showConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-6">
          <div className="w-full max-w-xs rounded-2xl bg-white p-6 shadow-xl">
            {submitting ? (
              <>
                <p className="mb-4 text-center text-base font-bold text-gray-800">足あとを記録中...</p>
                <LoadingSpinner />
              </>
            ) : (
              <>
                <p className="mb-1 text-center text-base font-bold text-gray-800">この内容で足あとを置きますか？</p>
                <p className="mb-5 text-center text-xs text-gray-500">足あとを置くと内容を修正できません。</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowConfirm(false)}
                    className="flex-1 rounded-full border border-gray-300 py-2 text-sm text-gray-600"
                  >
                    キャンセル
                  </button>
                  <button
                    onClick={handleConfirmedSubmit}
                    disabled={submitting}
                    className="flex-1 rounded-full bg-amber-500 py-2 text-sm font-bold text-white disabled:opacity-50"
                  >
                    足あとを置く！
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {stepBar}

      <div className={isCollapsed ? 'hidden' : 'contents'}>
      {/* ガイドテキスト + ツールバー */}
      <div className="px-4 py-2 border-b border-gray-100">
        <p className="text-xs text-center font-medium text-gray-600">指で自由に描いてね！イラストでも文字でもOK </p>
        {/* Undo / Redo / Clear */}
        <div className='flex justify-center gap-2 mt-2'>
          <button 
            onClick={handleUndo} 
            disabled={!canUndo}
            className="px-3 py-1 text-sm font-semibold rounded-full bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            ←
          </button>
          <button 
            onClick={handleRedo} 
            disabled={!canRedo}
            className="px-3 py-1 text-sm font-semibold rounded-full bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            →
          </button>
          <button 
            onClick={handleClear}
            disabled={!canClear}
            className="px-3 py-1 text-sm font-semibold rounded-full bg-red-400 text-white hover:bg-red-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            クリア
          </button>
        </div>
        {/* カラーパレット */}
        <div className="flex justify-center gap-2 mt-2 flex-wrap">
          {COLORS.map(({ color, label, border }) => (
            <button
              key={color}
              onClick={() => handleColorChange(color)}
              title={label}
              style={{ backgroundColor: color }}
              className={`w-7 h-7 rounded-full transition-transform hover:scale-110 ${
                border ? 'border border-gray-300' : ''
              } ${
                strokeColor === color
                  ? 'ring-2 ring-offset-1 ring-gray-500 scale-110'
                  : ''
              }`}
            />
          ))}
        </div>
      </div>

      {/* キャンバス */}
      <div className="flex-1 overflow-hidden bg-amber-50 relative">
        <canvas
          ref={canvasRef}
          className="touch-none"
          style={{ display: 'block', width: '100%', height: '100%' }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
        />
      </div>

      {/* エラー表示 + 投稿ボタン */}
      <div className="px-4 py-3 border-t border-gray-100">
        {canvasError && <p className="mb-2 text-xs text-red-500">{canvasError}</p>}
        {error && <p className="mb-2 text-xs text-red-500">{error}</p>}
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full rounded-full bg-amber-500 hover:bg-amber-600 py-3 font-bold text-white disabled:opacity-50 transition-colors"
        >
          足あとを置く！
        </button>
      </div>

      <div className="px-4 py-3 border-t border-gray-100">
        <button
          onClick={handleBack}
          className="w-full rounded-full bg-gray-400 hover:bg-gray-500 py-3 font-bold text-white transition-colors"
        >
          戻る
        </button>
      </div>
      </div>{/* isCollapsed wrapper */}
    </div>
  );
};

export default PostFormStep2Canvas;